import {
  SubmissionStatus,
  ReviewMode,
  SignatureType,
  ReviewerStatus,
} from "encamp-prisma/generated/browser";

/**
 * Structural type representing a submission reviewer with just the fields
 * needed to compute who we're waiting on. This is intentionally loose to
 * accept any object with at least these properties, and is generic over
 * the User type to preserve the actual type through the function.
 */
export type SubmissionReviewerForWaitingOn<TUser> = {
  isSigner: boolean;
  reviewOrder: number;
  user: TUser;
  completedAt: Date | null;
  reviewerStatus: ReviewerStatus | null;
};

export type SubmissionForWaitingOn<TUser> = {
  status: SubmissionStatus;
  reviewMode: ReviewMode;
  signatureType: SignatureType | null;
  reviewers: SubmissionReviewerForWaitingOn<TUser>[];
};

/**
 * Statuses where a submission can be "waiting on" reviewers.
 * - ENCAMP_INITIAL_REVIEW: staff reviewer must complete before sending to external reviewers
 * - IN_REVIEW: external reviewers (and signers) are actively reviewing
 */
export const REVIEW_STATUSES: SubmissionStatus[] = [
  SubmissionStatus.ENCAMP_INITIAL_REVIEW,
  SubmissionStatus.IN_REVIEW,
];

export function computeWaitingOnUsers<TUser>(submission: SubmissionForWaitingOn<TUser>): TUser[] {
  if (!REVIEW_STATUSES.includes(submission.status)) {
    return [];
  }

  const reviewers = submission.reviewers ?? [];

  if (reviewers.length === 0) {
    return [];
  }

  const requiresSignerGating =
    submission.signatureType === SignatureType.DIGITAL ||
    submission.signatureType === SignatureType.WET_INK;

  const eligibleNonSignersApproved = !requiresSignerGating
    ? true
    : reviewers
        .filter((r) => !r.isSigner)
        .every((r) => r.reviewerStatus === ReviewerStatus.APPROVED);

  const pendingReviewers = reviewers.filter((r) => {
    const isPending = r.completedAt == null && r.reviewerStatus == null;

    if (!isPending) {
      return false;
    }

    // For DIGITAL and WET_INK signatures: do not surface signers as "waiting on"
    // until all eligible non-signers have approved.
    if (requiresSignerGating && r.isSigner && !eligibleNonSignersApproved) {
      return false;
    }

    return true;
  });

  const pendingSorted = pendingReviewers.sort((a, b) => a.reviewOrder - b.reviewOrder);

  // ENCAMP_INITIAL_REVIEW: only the first pending reviewer (staff at order 0)
  // External reviewers haven't been notified yet, so they shouldn't appear as "waiting on"
  if (submission.status === SubmissionStatus.ENCAMP_INITIAL_REVIEW) {
    return pendingSorted.slice(0, 1).map((r) => r.user);
  }

  // IN_REVIEW: respect the submission's reviewMode
  const waitingReviewers =
    submission.reviewMode === ReviewMode.SEQUENTIAL ? pendingSorted.slice(0, 1) : pendingSorted;

  return waitingReviewers.map((r) => r.user);
}
